/**
 * Coverage & out-of-office (WPS-160 §1g) - mark a resolution specialist out
 * and name who covers their zones for a window. New claims that would route to
 * the out user divert to the cover only while the window is open, and revert
 * automatically once it lapses (the backend resolver honours the window; there
 * is no sweep). Entirely data-driven - a plain user->user substitution, no
 * tenant vocabulary. Options come from the org's members. Fixed chrome copy is
 * translated (keys under `claimCoverage.*` in seed_translations).
 */

import { useMemo, useState } from "react";
import { CalendarClock, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { ConfirmDeleteDialog } from "@/admin/components/confirm-delete-dialog";
import { toastApiError } from "@/shared/lib/api-error";
import { useForm, type FormField } from "@/shared/lib/forms";
import {
  useClaimCoverage,
  useCreateCoverage,
  useDeleteCoverage,
  type CoverageWindow,
} from "@/admin/hooks/use-claim-coverage";

function formatRange(start: string, end: string): string {
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  const s = new Date(start).toLocaleDateString(undefined, opts);
  const e = new Date(end).toLocaleDateString(undefined, opts);
  return `${s} - ${e}`;
}

export function CoverageSection() {
  const { t } = useTranslation();
  const { data, isPending } = useClaimCoverage(true);
  const create = useCreateCoverage();
  const remove = useDeleteCoverage();
  const [adding, setAdding] = useState(false);
  const [toDelete, setToDelete] = useState<CoverageWindow | null>(null);

  const memberOptions = useMemo(
    () => (data?.members ?? []).map((m) => ({ value: m.uuid, label: m.name })),
    [data?.members],
  );

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: "select",
        name: "out_user_uuid",
        label: t("claimCoverage.whoIsOut", "Who is out"),
        options: memberOptions,
        required: t("claimCoverage.whoIsOutRequired", "Select who is out"),
        testId: "coverage-out-user",
      },
      {
        type: "select",
        name: "covering_user_uuid",
        label: t("claimCoverage.whoIsCovering", "Who is covering"),
        options: memberOptions,
        required: t(
          "claimCoverage.whoIsCoveringRequired",
          "Select who is covering",
        ),
        testId: "coverage-covering-user",
      },
      {
        type: "datepicker",
        name: "starts_at",
        label: t("claimCoverage.from", "From"),
        required: t("claimCoverage.startRequired", "A start date is required"),
      },
      {
        type: "datepicker",
        name: "ends_at",
        label: t("claimCoverage.until", "Until"),
        required: t("claimCoverage.endRequired", "An end date is required"),
      },
      {
        type: "text",
        name: "reason",
        label: t("claimCoverage.reason", "Reason (optional)"),
      },
    ],
    [memberOptions, t],
  );

  const { Form } = useForm(
    { name: "coverage-form", submit: "footer", fields },
    async (raw) => {
      const v = raw as Record<string, unknown>;
      try {
        await create.mutateAsync({
          out_user_uuid: String(v.out_user_uuid),
          covering_user_uuid: String(v.covering_user_uuid),
          starts_at: String(v.starts_at),
          ends_at: String(v.ends_at),
          reason: v.reason ? String(v.reason) : undefined,
        });
        toast.success(t("claimCoverage.added", "Coverage added"));
        setAdding(false);
      } catch (err) {
        toastApiError(
          err,
          t("claimCoverage.addFailed", "Failed to add coverage"),
        );
      }
    },
    [fields],
  );

  const windows = data?.windows ?? [];

  return (
    <section>
      <div className="mb-3 flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display text-[18px] font-medium text-[var(--ink)]">
            {t("claimCoverage.title", "Coverage & out-of-office")}
          </h3>
          <p className="mt-1 max-w-[560px] text-[13px] text-[var(--ink-2)]">
            {t(
              "claimCoverage.description",
              "Mark a resolution specialist out and name who covers their zones. New claims route to the cover only while the window is open, then revert automatically - nothing to undo.",
            )}
          </p>
        </div>
        {!adding && (
          <Button
            size="sm"
            onClick={() => setAdding(true)}
            data-testid="coverage-new"
          >
            <Plus size={14} /> {t("claimCoverage.new", "New coverage")}
          </Button>
        )}
      </div>

      <div className="rounded-[8px] border-[0.5px] border-[var(--rule)]">
        {isPending ? (
          <div className="px-4 py-6 text-[13px] text-[var(--ink-3)]">
            {t("claimCoverage.loading", "Loading coverage...")}
          </div>
        ) : windows.length === 0 && !adding ? (
          <div className="flex flex-col items-center gap-1.5 px-4 py-8 text-center">
            <CalendarClock size={18} className="text-[var(--ink-3)]" />
            <p className="text-[13px] text-[var(--ink-3)]">
              {t("claimCoverage.empty", "No coverage windows. Everyone is in.")}
            </p>
          </div>
        ) : (
          windows.map((w) => (
            <div
              key={w.uuid}
              data-testid={`coverage-row-${w.uuid}`}
              className="flex items-center justify-between gap-3 border-b-[0.5px] border-[var(--rule-light)] px-4 py-3 last:border-b-0"
            >
              <div className="flex min-w-0 flex-wrap items-center gap-2 text-[13px]">
                <span className="text-[var(--ink)]">
                  <span className="font-medium">{w.covering_user?.name}</span>{" "}
                  {t("claimCoverage.covers", "covers")}{" "}
                  <span className="font-medium">{w.out_user?.name}</span>
                </span>
                <span className="text-[var(--ink-3)]">
                  {formatRange(w.starts_at, w.ends_at)}
                </span>
                {w.active && (
                  <Badge
                    variant="secondary"
                    data-testid="coverage-active-badge"
                  >
                    {t("claimCoverage.active", "Active")}
                  </Badge>
                )}
                {w.reason && (
                  <span className="text-[var(--ink-3)]">· {w.reason}</span>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t(
                  "claimCoverage.removeAria",
                  "Remove coverage for {{name}}",
                  {
                    name: w.out_user?.name,
                  },
                )}
                onClick={() => setToDelete(w)}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          ))
        )}

        {adding && (
          <div className="border-t-[0.5px] border-t-[var(--rule)] px-4 py-4">
            <Form className="space-y-4" />
            <div className="mt-3 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setAdding(false)}
              >
                {t("common.cancel", "Cancel")}
              </Button>
              <Button
                type="submit"
                form="coverage-form"
                disabled={create.isPending}
                data-testid="coverage-submit"
              >
                {create.isPending
                  ? t("claimCoverage.adding", "Adding...")
                  : t("claimCoverage.add", "Add coverage")}
              </Button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={toDelete !== null}
        onOpenChange={(next) => !next && setToDelete(null)}
        title={t("claimCoverage.removeTitle", "Remove coverage?")}
        description={
          toDelete
            ? t(
                "claimCoverage.removeDescription",
                "New claims will route to {{name}} again.",
                { name: toDelete.out_user?.name },
              )
            : undefined
        }
        isPending={remove.isPending}
        onConfirm={() => {
          if (!toDelete) return;
          remove.mutate(toDelete.uuid, {
            onSuccess: () => {
              setToDelete(null);
              toast.success(t("claimCoverage.removed", "Coverage removed"));
            },
            onError: (err) =>
              toastApiError(
                err,
                t("claimCoverage.removeFailed", "Failed to remove coverage"),
              ),
          });
        }}
      />
    </section>
  );
}
